import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useLocation, useParams } from "react-router-dom";
import posthog from "posthog-js";
import CONFIG from "config/config";
import { CB_EVENTS } from "campaign-builder/analytics/cbAnalytics";
import {
  getChannelConfig,
  hasChannelConfig,
  resolveBuilderChannel,
  type BuilderCampaignSnapshot,
  type BuilderStep,
  type StepCompletion,
} from "campaign-builder/config";
import { format } from "date-fns";
import {
  BuilderShell,
  BuilderPageSkeleton,
  BuilderErrorState,
  DatesWarningBanner,
  useLoadTimeout,
} from "campaign-builder/shell";
import { EDDM_SIGNUP } from "shared/constants";
import { applyStoredTheme, clearThemeClass } from "campaign-builder/theme";
import { ThemeCookieBridge } from "campaign-builder/shell/ThemeCookieBridge";
import { ThemedOadsSidebar } from "campaign-builder/ThemedOadsSidebar";
import { oadsSidebarFooterItems } from "campaign-builder/oadsSidebarFooter";
import {
  getDatePickerDateFormat,
  switchToEddm,
  isLaunchDateOutdated,
  interpolateLeadTime,
  deriveStepState,
  deriveLastActiveStep,
  BUILDER_STEPS,
} from "campaign-builder/state";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { useMinLeadTimeBusinessDays } from "campaign-builder/hooks";
import {
  MinBudgetModal,
  SaveStatusProvider,
  TargetingFocusContext,
  TargetingGateContext,
  TargetingProvider,
  TargetingValidationContext,
  type SaveStatus,
  type TargetingFocusRequest,
  type TargetingGate,
} from "campaign-builder/channels/flyering/targeting";
import {
  DesignStep,
  DesignSelectionContext,
  useHydrateSelectedDesign,
  getFlyerFormatMeta,
  type DesignView,
} from "campaign-builder/channels/flyering/design";
import {
  StaticTrackingStep,
  TrackingContext,
  useStaticTracking,
  type TrackingCampaignData,
} from "campaign-builder/channels/flyering/tracking";
import { OrderDetailsFlow } from "campaign-builder/channels/flyering/order-details";
import { useBillingGateSurface } from "campaign-builder/checkout/billing-profile/useBillingGateSurface";
import { useStore } from "store";
import { isClientEmailVerified } from "graphQL/mutations/user";
import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { UserMenu } from "campaign-builder/channels/directmail/adm/UserMenu";
import { ConfirmEmailModal } from "campaign-builder/channels/directmail/adm/order-details/ConfirmEmailModal";
import { AdmBuilder } from "campaign-builder/channels/directmail/adm/AdmBuilder";

const formatLaunchDate = (startDate?: string | null, countryCode?: string | null) =>
  startDate ? format(new Date(Number(startDate)), getDatePickerDateFormat(countryCode)) : "Pending";

// Summary "Total cost before taxes" figure (pre-tax subtotal), in the campaign's
// country currency. "—" until a city + areas are selected and the server cost
// lands, so a fresh builder never shows a zero figure.
const formatSubtotal = (subtotal?: number | null, currencySign = "$") =>
  subtotal && subtotal > 0
    ? `${currencySign}${subtotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

// AC35/AC36 — outdated-dates warning copy. {N} is interpolated with the OADS lead
// time so the day count is never hardcoded (epic T3).
const DATES_WARNING_H2H = {
  title: "Please, update dates",
  message:
    "Some of your selected distribution dates are no longer valid. Dates must be at least {N} business days in the future.",
  linkLabel: "Edit distribution dates",
} as const;
const DATES_WARNING_D2D = {
  title: "Please, update date",
  message:
    "Your campaign launch must be scheduled at least {N} business days from today. Please select a new launch date.",
  linkLabel: "Edit launch date",
} as const;

/**
 * Flyering (H2H/D2D) builder mount for the redesigned 3-step shell. Resolves the
 * channel from the loaded campaign and drives BuilderShell off its ChannelConfig.
 * ADM is served by AdmBuilder; CampaignBuilder below dispatches between them.
 */
const FlyeringBuilder = () => {
  const { campaignId, clientId } = useParams<{ campaignId: string; clientId: string }>();
  const {
    campaignData,
    isLoading,
    error,
    isReadOnly,
    refetchCampaign,
    setCampaignId,
    setClientId,
    updateCampaignState,
  } = useCampaignData();
  const { client, country, isLoading: isClientConfigLoading } = useClientConfig();
  const { user, updateUser } = useStore();
  const { pathname } = useLocation();
  const history = useHistory();

  const [activeStep, setActiveStep] = useState<BuilderStep>("targeting");
  // Billing-profile gate, shown on every builder step.
  const billingGate = useBillingGateSurface(clientId);
  // AC6 — a brief skeleton on user-initiated step switches (the initial-load and
  // resume skeleton is the whole-page BuilderPageSkeleton below). Set in
  // handleStepChange; cleared shortly after so content fades in rather than pops.
  const [stepLoading, setStepLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [validationVersion, setValidationVersion] = useState(0);
  // The Targeting step's hard gates, reported up from the TargetingProvider (F8).
  const [targetingGate, setTargetingGate] = useState<TargetingGate | null>(null);
  const [budgetModalOpen, setBudgetModalOpen] = useState(false);
  // F12 — the Order Details drawer, opened by Review-and-finish on the final step.
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
  // F14 — the email-verification gate shown before the drawer for unverified accounts.
  const [confirmEmailOpen, setConfirmEmailOpen] = useState(false);
  // F9 — a focus request sent down to the Targeting step when the Edit-dates link
  // is clicked, revealing the affected launch-date field / distribution area.
  const [focusRequest, setFocusRequest] = useState<TargetingFocusRequest | null>(null);
  // F10 — the design selected on the Design step. Owned here so step navigation can
  // read "has a design"; hydrated from the campaign's assigned design on reload.
  const [selectedDesign, setSelectedDesign] = useState<DesignView | null>(null);
  const { flyerInfo, isLoading: flyerInfoLoading } = useGetFlyerInfo();
  const { hydrated: designHydrated } = useHydrateSelectedDesign({
    clientId,
    flyerInfo,
    selectedDesign,
    setSelectedDesign,
  });
  // An in-session selection reflects immediately; activeRoomId only bridges the
  // reload window BEFORE hydration resolves the assigned design into selectedDesign.
  // Once hydrated, selectedDesign is authoritative, so a de-select (which doesn't
  // clear the server link) correctly drops "has a design" and re-gates the step.
  const hasDesign = !!selectedDesign || (!!flyerInfo?.activeRoomId && !designHydrated);

  // F11 — the static Tracking form, owned here (above the shell) so it survives step
  // switches; the step would otherwise lose its state on unmount and re-hydrate from
  // the cached campaign, reverting unsaved edits. Persists write through to the cache.
  const onTrackingPersisted = useCallback(
    (patch: TrackingCampaignData) => {
      if (campaignData) updateCampaignState({ ...campaignData, ...patch } as typeof campaignData);
    },
    [campaignData, updateCampaignState]
  );
  const tracking = useStaticTracking({ campaignId, campaignData, onPersisted: onTrackingPersisted });
  const trackingValid = tracking.isValid;

  const designSelection = useMemo(() => ({ selectedDesign, setSelectedDesign }), [selectedDesign]);
  const targetingValidation = useMemo(() => ({ version: validationVersion }), [validationVersion]);
  const gateApi = useMemo(() => ({ report: setTargetingGate }), []);
  // The focus request is a one-shot: consumers clear it after revealing the control
  // so it can't replay when the Targeting step remounts on a later visit (PAD-31560).
  const focusApi = useMemo(() => ({ request: focusRequest, clear: () => setFocusRequest(null) }), [focusRequest]);
  const minLeadBusinessDays = useMinLeadTimeBusinessDays();

  // Advancing with an incomplete step bumps the validation signal so targeting cards
  // surface their field errors, then scrolls to the first card.
  const handleRequestValidation = useCallback((step: BuilderStep) => {
    setValidationVersion((version) => version + 1);
    if (step !== "targeting") return;
    requestAnimationFrame(() => {
      document
        .querySelector('[data-targeting-section="details"]')
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, []);

  // F14 — Review-and-finish gates on email verification before the drawer. Verified
  // accounts open the drawer directly; unverified ones get the Confirm-your-email
  // modal. The store's isVerified isn't hydrated in the builder, so re-check the real
  // status first (and cache it on success) before falling back to the modal.
  // Guards against a double-click firing two concurrent verification checks: if one
  // resolved to the modal and the other to the drawer, both could open at once.
  const verifyCheckInFlight = useRef(false);
  const handleReviewAndFinish = useCallback(async () => {
    if (user?.isVerified === true) {
      setOrderDetailsOpen(true);
      return;
    }
    if (verifyCheckInFlight.current) return;
    verifyCheckInFlight.current = true;
    try {
      const response = await isClientEmailVerified();
      const {
        data: { getClientEmailVerified },
      } = await (response as Response).json();
      if (getClientEmailVerified?.success) {
        updateUser({ isVerified: true });
        setOrderDetailsOpen(true);
        return;
      }
    } catch (error) {
      // fall through to the modal rather than bypass the gate
    } finally {
      verifyCheckInFlight.current = false;
    }
    setConfirmEmailOpen(true);
  }, [user?.isVerified, updateUser]);

  // Stable so ConfirmEmailModal's 2-second success auto-advance isn't reset by an
  // unrelated parent re-render (e.g. a save-status tick) during the success window.
  const handleEmailVerified = useCallback(() => {
    setConfirmEmailOpen(false);
    setOrderDetailsOpen(true);
  }, []);

  useEffect(() => {
    setCampaignId(campaignId);
    setClientId(clientId);
  }, [campaignId, clientId, setCampaignId, setClientId]);

  const builderChannel = useMemo(
    () => resolveBuilderChannel({ channel: campaignData?.channel, subtype: campaignData?.subtype }),
    [campaignData?.channel, campaignData?.subtype]
  );
  // Gate on hasChannelConfig: a valid BuilderChannel without a registered config
  // (e.g. EDDM) would otherwise make getChannelConfig throw and crash the tree.
  const config = useMemo(
    () => (builderChannel && hasChannelConfig(builderChannel) ? getChannelConfig(builderChannel) : null),
    [builderChannel]
  );
  // The targeting provider only models the two flyering channels. Narrow here
  // (rather than casting at the prop) so any other channel reaching this mount
  // resolves to null and is treated as unsupported below — loud, not silently
  // forwarded as a bad channel value.
  const targetingChannel = builderChannel === "H2H" || builderChannel === "D2D" ? builderChannel : null;

  // The oads-sidebar menu spans all three frontends. From the builder, cross-frontend
  // items (platform / next) are absolute URLs → full-page <a>; builder-local routes
  // navigate client-side via react-router.
  const renderSidebarLink = useCallback(
    ({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) => {
      const builderBase = CONFIG.BUILDER_BASE_URL.replace(/\/+$/, "");
      const isExternal = /^https?:\/\//i.test(href) && !href.startsWith(builderBase);
      if (isExternal) {
        return (
          <a href={href} className={className}>
            {children}
          </a>
        );
      }
      let to = href.startsWith(builderBase) ? href.slice(builderBase.length) : href;
      if (!to.startsWith("/")) to = `/${to}`;
      return (
        <Link to={to} className={className}>
          {children}
        </Link>
      );
    },
    []
  );

  const settled = !isLoading && !isClientConfigLoading;
  // A load that hangs (resolves to neither data nor error) shows the error state
  // instead of an endless skeleton.
  const loadTimedOut = useLoadTimeout(!settled);
  // Campaign loaded but no config for its channel (e.g. a non-H2H/D2D campaign
  // reached this route) — treat as an unrecoverable error, not an endless skeleton.
  const unsupportedChannel = settled && !!campaignData && (!config || !targetingChannel);
  const hasError = !!error || unsupportedChannel || loadTimedOut;
  const dataLoaded = settled && !!campaignData && !!client && !!country && !!config;

  // Channel summary units reported up from the Targeting provider (F15): H2H
  // flyers / D2D letterboxes. Fed into both snapshot fields so the channel's
  // summaryUnits.getValue reads the right one regardless of channel.
  const summaryUnits = targetingGate?.summaryUnits ?? 0;
  const snapshot: BuilderCampaignSnapshot & {
    hasDesign: boolean;
    trackingValid: boolean;
    flyersAmount: number;
    lettersAmount: number;
  } = useMemo(
    () => ({
      id: campaignData?.id ?? null,
      channel: campaignData?.channel,
      subtype: campaignData?.subtype,
      flyerType: campaignData?.flyerType,
      startDate: campaignData?.startDate,
      campaignName: campaignData?.campaignName,
      hasDesign,
      trackingValid,
      flyersAmount: summaryUnits,
      lettersAmount: summaryUnits,
    }),
    [campaignData, hasDesign, trackingValid, summaryUnits]
  );

  // AC31/AC32 — fold the reported Targeting gate into shell signals. Targeting
  // is incomplete while the minimum isn't met or the maximum is exceeded; the
  // CTA is disabled (with the hover tooltip) only for the min case — the sole
  // disabled-CTA case (AC4).
  const baseTargetingComplete = config ? config.getStepCompletion(snapshot).targeting : false;
  const targetingComplete =
    baseTargetingComplete && (targetingGate ? targetingGate.minMet && !targetingGate.maxExceeded : true);
  const completionOverride = useMemo<Partial<StepCompletion>>(
    () => ({ targeting: targetingComplete }),
    [targetingComplete]
  );
  const primaryActionBlock =
    activeStep === "targeting" && targetingGate?.citySelected && !targetingGate.minMet && targetingGate.minTooltip
      ? { disabled: true, tooltip: targetingGate.minTooltip }
      : undefined;

  // AC8 — resume at the first step the loaded data leaves incomplete. The
  // persisted cursor is deliberately NOT trusted past that: legacy drafts wrote
  // "design" on merely ENTERING the design phase (flyer-type pick, "design it
  // later"), so cursor-driven forward jumps could land on a step whose
  // prerequisites are incomplete — an unreachable step per deriveStepState.
  // PAD-32157 — Declarative capture helper. Fires once per step entry via the
  // callback path that ORIGINATED the transition (handleStepChange for user
  // clicks, the resume effect below for a restored draft), not by observing
  // `activeStep` changes. Follows the "action → reaction" convention the tech
  // lead flagged (prefer callbacks over imperative effects that listen to state).
  const captureStepReached = useCallback(
    (step: BuilderStep) => {
      if (!campaignId || !clientId) return;
      const base = { campaign_id: campaignId, client_id: clientId };
      if (step === "targeting") posthog.capture(CB_EVENTS.AUDIENCE_REACHED, base);
      else if (step === "design") posthog.capture(CB_EVENTS.DESIGN_REACHED, base);
      else if (step === "tracking") {
        posthog.capture(CB_EVENTS.SUBMIT_REACHED, { ...base, total_cost: targetingGate?.subtotal ?? 0 });
      }
    },
    [campaignId, clientId, targetingGate?.subtotal]
  );

  // Runs once, after the campaign + config settle.
  const stepRestored = useRef(false);
  useEffect(() => {
    // Wait for the Targeting gate to report AND its saved selection to hydrate,
    // so `derived` reflects the real area/min-gate state — the gate's first
    // report fires before the saved areas load, which would misread a completed
    // Targeting step as incomplete. Also wait for the flyer info to settle: it
    // backs `hasDesign`, so resuming before it loads would land a design-complete
    // draft on Design instead of Tracking.
    if (stepRestored.current || !dataLoaded || !config || !targetingGate?.hydrated || flyerInfoLoading) return;
    stepRestored.current = true;
    const resumedStep = deriveStepState(snapshot, config, completionOverride).currentStep;
    setActiveStep(resumedStep);
    // Emit the milestone for the landing step (initial mount or resume) — this is
    // still an action ("the builder just resumed on step X"), routed through the
    // shared captureStepReached callback to keep the emit path single-source.
    captureStepReached(resumedStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, targetingGate, flyerInfoLoading]);

  // AC8 — persist the furthest contiguously-completed step into `lastActiveStep`
  // so the platform campaign cards' progress dots + Resume link reflect builder
  // progress (matching ADM). Forward-only; the terminal "submit" value is owned by
  // the submit flow and never rewound. Re-runs as the gate/selection completes a step.
  useEffect(() => {
    // Same gate dependency as the resume effect: persist only once the Targeting
    // gate is known, so a named-but-empty campaign isn't recorded as past Targeting.
    if (!dataLoaded || !campaignData || !config || isReadOnly || !targetingGate) return;
    const cursor = config.lastActiveStep;
    if (!cursor) return;
    const stored = campaignData.lastActiveStep ?? null;
    if (cursor.terminalValues?.includes(stored ?? "")) return;
    const { completion } = deriveStepState(snapshot, config, completionOverride);
    const next = deriveLastActiveStep(completion, cursor);
    const order = [cursor.initial, ...BUILDER_STEPS.map((step) => cursor.byCompletedStep[step]).filter(Boolean)];
    if (order.indexOf(next) > order.indexOf(stored ?? cursor.initial)) {
      updateCampaignExtraData({ campaignId: campaignData.id, lastActiveStep: next }).catch(() => undefined);
      updateCampaignState({ ...campaignData, lastActiveStep: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, isReadOnly, snapshot, config, completionOverride]);

  // AC6 — clear the step skeleton shortly after a switch. Triggered only by
  // user-initiated step changes (handleStepChange), not the initial load or the
  // resume jump — those are covered by the whole-page BuilderPageSkeleton, so a
  // returning user doesn't get a second skeleton flash on top of it.
  useEffect(() => {
    if (!stepLoading) return;
    const timer = setTimeout(() => setStepLoading(false), 300);
    return () => clearTimeout(timer);
  }, [stepLoading]);

  // AC33 — leaving Targeting forward while under the market minimum opens the
  // EDDM modal instead of advancing.
  const handleStepChange = useCallback(
    (next: BuilderStep) => {
      if (activeStep === "targeting" && next !== "targeting" && targetingGate?.belowMinBudget) {
        setBudgetModalOpen(true);
        return;
      }
      if (next === activeStep) return;
      setStepLoading(true);
      setActiveStep(next);
      // PAD-32157 — emit the milestone as part of the transition action, not
      // reactively via a useEffect on activeStep.
      captureStepReached(next);
    },
    [activeStep, targetingGate?.belowMinBudget, captureStepReached]
  );

  // AC35/AC36 — a re-entered draft whose dates now fall in the past / within the
  // lead time. H2H per-area dates are reported up by the TargetingProvider; D2D's
  // single launch date is read here from the snapshot. The warning shows below the
  // Summary on every step and blocks Review-and-finish (isSubmitBlocked) until fixed.
  const isD2d = builderChannel === "D2D";
  const datesOutdated =
    (isD2d && isLaunchDateOutdated(campaignData?.startDate, minLeadBusinessDays)) || !!targetingGate?.datesOutdated;
  const datesWarningCopy = isD2d ? DATES_WARNING_D2D : DATES_WARNING_H2H;

  // The Edit link returns to Targeting and asks it to reveal the affected control:
  // the D2D launch-date field, or the first outdated H2H area card.
  const handleEditDates = useCallback(() => {
    setActiveStep("targeting");
    // PAD-32157 — route re-entering Targeting through the shared capture path.
    captureStepReached("targeting");
    setFocusRequest((prev) => ({
      target: isD2d ? "launchDate" : "distributionAreas",
      areaId: targetingGate?.outdatedAreaId ?? null,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
  }, [isD2d, targetingGate?.outdatedAreaId, captureStepReached]);

  const datesWarning = datesOutdated ? (
    <DatesWarningBanner
      title={datesWarningCopy.title}
      message={interpolateLeadTime(datesWarningCopy.message, minLeadBusinessDays)}
      linkLabel={datesWarningCopy.linkLabel}
      onEdit={handleEditDates}
    />
  ) : undefined;

  let content: React.ReactNode;
  if (hasError) {
    // Any fatal load failure (fetch error, unsupported channel, or a hung load) →
    // the shared generic error state instead of an endless skeleton (PAD-31535).
    // Retry covers the campaign fetch (the dominant failure); the client-config
    // hooks don't expose full refetch handles, so a hang there resolves on reload.
    content = <BuilderErrorState onRetry={() => void refetchCampaign()} />;
  } else if (!dataLoaded || !config || !targetingChannel) {
    content = <BuilderPageSkeleton />;
  } else {
    content = (
      <SaveStatusProvider onStatus={setSaveStatus}>
        <DesignSelectionContext.Provider value={designSelection}>
          <TrackingContext.Provider value={tracking}>
            <TargetingValidationContext.Provider value={targetingValidation}>
              <TargetingFocusContext.Provider value={focusApi}>
                <TargetingGateContext.Provider value={gateApi}>
                  {/*
                The targeting provider lives ABOVE the shell so its state (selected
                city + areas, recommended zones) survives step switches — leaving
                Targeting for Design/Tracking and back no longer remounts it, so the
                map and selection return instantly with no refetch (PAD-31560). The
                channel is H2H/D2D here (this mount serves only those); ADM uses its
                own builder. The targeting step renders only its content now.
              */}
                  <TargetingProvider channel={targetingChannel}>
                    <BuilderShell
                      config={config}
                      campaign={snapshot}
                      activeStep={activeStep}
                      onStepChange={handleStepChange}
                      stepComponents={{ design: DesignStep, tracking: StaticTrackingStep }}
                      completionOverride={completionOverride}
                      primaryActionBlock={primaryActionBlock}
                      isSubmitBlocked={datesOutdated}
                      isLoading={stepLoading}
                      summary={{
                        totalCost: formatSubtotal(targetingGate?.subtotal, country?.currencySign),
                        campaignName: campaignData?.campaignName || "Pending",
                        launchDate: formatLaunchDate(campaignData?.startDate, country?.code),
                        audiences: "Pending",
                        format:
                          getFlyerFormatMeta(campaignData?.flyerType)?.label || campaignData?.flyerType || "Pending",
                        saveStatus,
                        warning: datesWarning,
                        launchDateWarning: isD2d && datesOutdated,
                      }}
                      onPrimaryAction={handleReviewAndFinish}
                      onRequestValidation={handleRequestValidation}
                      headerRight={<UserMenu clientId={clientId} companyName={client?.name} />}
                      topBanner={billingGate.renderBanner("builder-step")}
                    />
                    {billingGate.drawer}
                    <MinBudgetModal
                      open={budgetModalOpen}
                      onOpenChange={setBudgetModalOpen}
                      channelLabel={config.label}
                      minBudgetFormatted={targetingGate?.minBudgetFormatted ?? ""}
                      onSwitchToEddm={() => switchToEddm({ clientId: clientId ?? "", push: history.push })}
                    />
                    <ConfirmEmailModal
                      open={confirmEmailOpen}
                      onClose={() => setConfirmEmailOpen(false)}
                      onVerified={handleEmailVerified}
                    />
                    <OrderDetailsFlow
                      open={orderDetailsOpen}
                      onClose={() => setOrderDetailsOpen(false)}
                      config={config}
                      clientId={clientId ?? ""}
                    />
                  </TargetingProvider>
                </TargetingGateContext.Provider>
              </TargetingFocusContext.Provider>
            </TargetingValidationContext.Provider>
          </TrackingContext.Provider>
        </DesignSelectionContext.Provider>
      </SaveStatusProvider>
    );
  }

  return (
    <div className="flex h-screen w-full overflow-hidden">
      <ThemedOadsSidebar
        baseUrls={{
          platform: CONFIG.PLATFORM_BASE_URL,
          next: CONFIG.NEXT_BASE_URL,
          builder: CONFIG.BUILDER_BASE_URL,
        }}
        clientId={clientId ?? ""}
        client={{
          // Channel visibility (PAD-31735): country + the client's self-serve
          // flag + DM-campaign presence (parity with platform-next).
          country: country?.code,
          isSelfServeEnabled: client?.isSelfServeEnabled ?? true,
          hasDmCoreCampaigns: client?.hasDmCoreCampaigns ?? false,
          isEddmOnly: client?.createdBy === EDDM_SIGNUP,
        }}
        currentPath={pathname}
        footerItems={oadsSidebarFooterItems}
        eddmLogo={client?.createdBy === EDDM_SIGNUP}
        renderLink={renderSidebarLink}
      />
      <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">{content}</main>
    </div>
  );
};

/**
 * Unified builder mount: the single entry point for every redesigned channel.
 * Resolves the channel from the loaded campaign and hands off to the channel's
 * builder — ADM to AdmBuilder, H2H/D2D (and unsupported channels) to the flyering
 * mount. Before the campaign loads the channel is unknown, so the flyering mount
 * renders the shared loading skeleton until the resolved channel takes over.
 */
export const CampaignBuilder = () => {
  const { campaignData } = useCampaignData();
  const { user } = useStore();
  const builderChannel = resolveBuilderChannel({ channel: campaignData?.channel, subtype: campaignData?.subtype });

  // Dark mode is scoped to the redesigned builder: apply the persisted theme on
  // mount (covers SPA navigation into the builder, where the index.html
  // bootstrap didn't rerun) and drop it on unmount so legacy pages stay light.
  // Layout effect: runs before paint, so the entry frame is already dark.
  useLayoutEffect(() => {
    applyStoredTheme();
    return () => clearThemeClass();
  }, []);

  // PAD-32157 — register the resolved channel as a session-wide property so every
  // subsequent event carries it automatically. Fires once per resolved channel;
  // re-fires on channel change (e.g. draft re-opened as a different subtype).
  // Cleanup: unregister on unmount so `channel` doesn't leak into events emitted
  // by non-builder routes (dashboard, settings, etc.) after the user navigates
  // away — otherwise unrelated events get mis-attributed to the last builder
  // channel the user visited in the session.
  useEffect(() => {
    if (!builderChannel) return;
    posthog.register({ channel: builderChannel });
    return () => {
      posthog.unregister("channel");
    };
  }, [builderChannel]);

  // PAD-32157 — identify the logged-in user. The redesign shell does not import
  // the legacy header (which is where identify used to run), so events were
  // otherwise landing anonymous and breaking user-level funnels.
  useEffect(() => {
    if (!user?.id) return;
    posthog.identify(String(user.id), { email: user.email });
  }, [user?.id, user?.email]);

  return (
    <>
      <ThemeCookieBridge />
      {builderChannel === "ADM" ? <AdmBuilder /> : <FlyeringBuilder />}
    </>
  );
};
